import { useRef, useState } from 'react'
import { Download, FileSpreadsheet, Sheet, UploadCloud } from 'lucide-react'
import type { ClientDraft, ParsedTable } from './types'
import { parseFile, looksLikeOnboardSheet } from './parse'
import { ImportReview } from './ImportReview'
import { downloadClientTemplate } from '../../../lib/download-template'

const ACCEPT = '.csv,.tsv,.txt,.xlsx,.xlsm,.xlsb,.xls,.ods'

interface FileImportIntakeProps {
  onImport: (drafts: ClientDraft[]) => void
  isSubmitting: boolean
  /** Offered when the dropped sheet carries team/task columns this importer would ignore. */
  onSwitchToOnboard?: () => void
}

export function FileImportIntake({ onImport, isSubmitting, onSwitchToOnboard }: FileImportIntakeProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [table, setTable] = useState<ParsedTable | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [parsing, setParsing] = useState(false)
  const [dragging, setDragging] = useState(false)

  const handleFile = async (file: File) => {
    setError(null)
    setParsing(true)
    try {
      const parsed = await parseFile(file, { sheetHint: /client/i })
      if (!parsed || parsed.rows.length === 0) {
        setError('No data rows found. The file needs a header row plus at least one record.')
        setTable(null)
      } else {
        setTable(parsed)
      }
    } catch (err) {
      setError(err instanceof Error ? `Could not read the file: ${err.message}` : 'Could not read the file.')
      setTable(null)
    } finally {
      setParsing(false)
    }
  }

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (file) handleFile(file)
  }

  if (table) {
    return (
      <>
        {onSwitchToOnboard && looksLikeOnboardSheet(table.headers) && (
          <div className="intake-banner intake-banner--warn" style={{ marginBottom: 'var(--space-2)' }}>
            <Sheet size={15} />
            <span>
              This sheet carries <strong>team member and task columns</strong>, which this importer ignores —
              it only creates the client records.{' '}
              <button type="button" className="btn btn-ghost btn-sm" onClick={onSwitchToOnboard}>
                Use Onboard import instead
              </button>
            </span>
          </div>
        )}
        <ImportReview
          table={table}
          onImport={onImport}
          onReset={() => {
            setTable(null)
            setError(null)
            if (inputRef.current) inputRef.current.value = ''
          }}
          isSubmitting={isSubmitting}
        />
      </>
    )
  }

  return (
    <div className="intake-import-empty">
      <button
        type="button"
        className={`intake-dropzone${dragging ? ' intake-dropzone--active' : ''}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        <span className="intake-dropzone-icon">
          {parsing ? <FileSpreadsheet size={26} /> : <UploadCloud size={26} />}
        </span>
        <span className="intake-dropzone-title">
          {parsing ? 'Reading file…' : 'Drop a spreadsheet or click to browse'}
        </span>
        <span className="intake-dropzone-sub">CSV, TSV, or Excel (.xlsx, .xls) · up to 200 rows</span>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) handleFile(file)
          }}
        />
      </button>

      {error && <p className="intake-errors intake-errors--block">{error}</p>}

      <div className="intake-help">
        <p className="intake-eyebrow">How it maps</p>
        <p className="intake-help-text">
          The first row is treated as headers. We auto-match columns like <code>name</code>,{' '}
          <code>vertical</code>, <code>plan</code>, <code>mrr</code>, <code>website</code>, and{' '}
          <code>status</code> — you can correct any mapping before importing. Only <strong>name</strong> is
          required.
        </p>
        <button type="button" className="btn btn-ghost btn-sm" onClick={downloadClientTemplate} style={{ marginTop: 'var(--space-1)' }}>
          <Download size={14} /> Download template CSV
        </button>
      </div>
    </div>
  )
}
